import React, { useState, useEffect } from "react";
import {
  IconButton,
  Popover,
  Box,
  Typography,
  Select,
  MenuItem,
  Button,
  FormControl,
  InputLabel,
  CircularProgress,
  Icon,
  IconButton as MuiIconButton,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import apiClient from "../../services/apiClient";
import toast from "react-hot-toast";
import { Pause, PlayArrow } from "@mui/icons-material";
import CustomInputBox from "../Form/CustomInputBox";
import { FaLink } from "react-icons/fa6";

/**
 * AgentSettingsWidget
 *
 *   • Pulls available languages / voices from the API
 *   • Loads the agent’s current settings
 *   • Lets the user change language, voice, website URL and upload a file
 *   • When the language changes, the voice selector automatically
 *     defaults to the first voice that belongs to the new language
 */
const AgentSettingsWidget = () => {
  const agentId = "68b04fe3379ae47560e2eedb";

  /* ---------- UI State ---------- */
  const [anchorEl, setAnchorEl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [playingId, setPlayingId] = useState(null);
  const [audio, setAudio] = useState(null);

  /* ---------- Data State ---------- */
  const [languageData, setLanguageData] = useState([]);
  const [voiceData, setVoiceData] = useState([]);

  // formData holds the current values being edited
  const [formData, setFormData] = useState({
    assigned_language_type: "",
    assigned_voice_type: "",
    file: null,
    importUrl: "",
    files: [], // existing files that came with the agent
  });

  // initialData is the snapshot we got from the server – used to detect changes
  const [initialData, setInitialData] = useState({});

  /* ---------- Popover helpers ---------- */
  const open = Boolean(anchorEl);
  const id = open ? "settings-popover" : undefined;
  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  /* ---------- API Calls ---------- */
  const fetchVoice = async () => {
    try {
      const response = await apiClient.get("/voice-codes");
      setVoiceData(response?.data || []);
    } catch (error) {
      console.error("Voice fetch failed:", error);
    }
  };

  const fetchLanguage = async () => {
    try {
      const response = await apiClient.get("/languages");
      setLanguageData(response?.data || []);
    } catch (error) {
      console.error("Language fetch failed:", error);
    }
  };

  const fetchAgentDetails = async () => {
    try {
      const response = await apiClient.get(`/agent-details-id/${agentId}`);
      const data = response.data;

      const voiceId = data?.assigned_voice_type?.id || "";
      const languageId = data?.assigned_language_type?.id || "";
      let importUrl = "";
      const files = [];

      data?.sources?.forEach((source) => {
        if (source.type === "website") {
          importUrl = source.content;
        } else if (source.type === "file") {
          files.push(source.content);
        }
      });

      const base = {
        assigned_language_type: languageId,
        assigned_voice_type: voiceId,
        importUrl,
        files,
      };

      setFormData(base);
      setInitialData(base);
    } catch (error) {
      console.error("Agent details fetch failed:", error);
    }
  };

  useEffect(() => {
    fetchVoice();
    fetchLanguage();
    fetchAgentDetails();
  }, []);

  /* ---------- Helpers ---------- */
  const hasChanges =
    formData.assigned_language_type !== initialData.assigned_language_type ||
    formData.assigned_voice_type !== initialData.assigned_voice_type ||
    formData.importUrl !== initialData.importUrl ||
    formData.file !== initialData.file ||
    JSON.stringify(formData.files) !== JSON.stringify(initialData.files);

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  /* ---------- Language change handler ---------- */
  const handleLanguageChange = (event) => {
    const newLangId = event.target.value;

    // Find the first voice that belongs to the newly‑selected language
    const firstVoice = voiceData.find(
      (v) => v.language._id === newLangId
    );

    setFormData((prev) => ({
      ...prev,
      assigned_language_type: newLangId,
      assigned_voice_type: firstVoice ? firstVoice._id : "", // default to first voice
    }));
  };

  /* ---------- Submit ---------- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = new FormData();
      payload.append("assigned_language_type", formData.assigned_language_type);
      payload.append("assigned_voice_type", formData.assigned_voice_type);
      payload.append("website_url", formData.importUrl);

      if (formData.file) {
        const filesArray = Array.isArray(formData.file)
          ? formData.file
          : [formData.file];
        filesArray.forEach((file) => payload.append("files[]", file));
      }

      await apiClient.put(`/update-agent-details/${agentId}`, payload);
      setInitialData(formData);
      toast.success("Agent settings updated successfully");
      handleClose();
    } catch (error) {
      console.error("Error saving request:", error);
      toast.error(
        error?.response?.data?.message || "Failed to update agent settings"
      );
    } finally {
      setLoading(false);
    }
  };

  /* ---------- Audio play ---------- */
  const handlePlay = (ele) => {
    if (audio) {
      audio.pause();
      setAudio(null);
      setPlayingId(null);
    }

    if (playingId !== ele._id) {
      const newAudio = new Audio(
        `${process.env.REACT_APP_BASE_URL}/${ele.voice_sample.replace(/\\/g, "/")}`
      );
      newAudio.play();
      setAudio(newAudio);
      setPlayingId(ele._id);

      newAudio.onended = () => {
        setPlayingId(null);
        setAudio(null);
      };
    }
  };

  /* ---------- Render ---------- */
  return (
    <>
      <IconButton onClick={handleClick}>
        <SettingsIcon />
      </IconButton>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box sx={{ p: 2, width: 350 }}>
          <Typography variant="h6" gutterBottom>
            Agent Settings
          </Typography>

          {/* Language selector */}
          <FormControl fullWidth margin="normal">
            <InputLabel>Language</InputLabel>
            <Select
              value={formData.assigned_language_type}
              onChange={handleLanguageChange}   // <-- updated
              label="Language"
            >
              {languageData.map((lang) => (
                <MenuItem key={lang._id} value={lang._id}>
                  {lang.language_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Voice selector */}
          <FormControl fullWidth margin="normal">
            <InputLabel>Voice</InputLabel>
            <Select
              name="assigned_voice_type"
              label="Voice"
              value={formData.assigned_voice_type || ""}
              onChange={handleChange("assigned_voice_type")}
              renderValue={(selected) => {
                const voice = voiceData.find((v) => v._id === selected);
                return voice
                  ? `${voice.gender} – ${voice.voice_name}`
                  : "Choose AI Voice";
              }}
            >
              {voiceData
                .filter((v) => v.language._id === formData.assigned_language_type)
                .map((v) => (
                  <MenuItem
                    key={v._id}
                    value={v._id}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Box>
                      <Typography variant="body1" fontWeight={500}>
                        {v.gender} – {v.voice_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {v.language.language_name}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePlay(v);
                      }}
                    >
                      {playingId === v._id ? <Pause /> : <PlayArrow />}
                    </IconButton>
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          {/* Website URL */}
          <FormControl fullWidth margin="normal">
            <CustomInputBox
              placeholder=""
              type="text"
              required={true}
              name="importUrl"
              borderRadius="4px"
              margin="20px 0px"
              backgroundColor="white"
              variant="outlined"
              label="Website URL"
              value={formData.importUrl || ""}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  importUrl: e.target.value,
                }))
              }
            />
          </FormControl>

          {/* File upload */}
          <FormControl fullWidth margin="normal">
            <CustomInputBox
              placeholder=""
              type="file"
              required={true}
              name="file"
              height="45px"
              borderRadius="4px"
              margin="15px 0px"
              backgroundColor="white"
              variant="outlined"
              label="Choose File"
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  file: e.target.files[0],
                }))
              }
            />

            {Array.isArray(formData.files) && formData.files.length > 0 && (
              <Box mt={1}>
                {formData.files.map((f, i) => (
                  <Typography
                    key={i}
                    variant="body2"
                    color="text.secondary"
                    sx={{ wordBreak: "break-all" }}
                  >
                    <FaLink /> Existing file:{" "}
                    <a
                      href={`${process.env.REACT_APP_BASE_URL}/${f}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {f.split("/").pop()}
                    </a>
                  </Typography>
                ))}
              </Box>
            )}
          </FormControl>

          {/* Save button */}
          <Box mt={2} textAlign="right">
            <Button
              variant="contained"
              disabled={!hasChanges || loading}
              onClick={handleSubmit}
            >
              {loading ? <CircularProgress size={24} /> : "Save"}
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default AgentSettingsWidget;
